import { useEffect, useMemo, useState } from 'react';
import { Repeat } from 'lucide-react';
import { useI18n } from '@/lib/i18n';
import { useAuth } from '@/hooks/useAuth';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { showToast } from '@/components/ui/toaster';
import { BottomSheet } from '@/components/common/BottomSheet';
import DatePicker from '@/components/common/DatePicker';
import { shiftSwapService, memberService } from '@/services/hr-request.service';

interface Props {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onCreated?: () => void;
    defaultDate?: string;
}

// Form pegawai untuk minta tukar shift di satu tanggal dengan rekan kerja.
export default function ShiftSwapSheet({ open, onOpenChange, onCreated, defaultDate }: Props) {
    const { t } = useI18n();
    const { user } = useAuth();
    const { currentWorkspace } = useWorkspace();
    const [members, setMembers] = useState<any[]>([]);
    const [targetUserId, setTargetUserId] = useState('');
    const [swapDate, setSwapDate] = useState(defaultDate || '');
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!open || !currentWorkspace) return;
        setSwapDate(defaultDate || '');
        memberService.list(currentWorkspace._id).then((res: any) => {
            const list = ((res.data as any[]) || [])
                .map((m: any) => m.user || m)
                .filter((u: any) => u && (u._id || u.id) && (u._id || u.id) !== user?._id);
            setMembers(list);
        }).catch(() => {});
    }, [open, currentWorkspace?._id, defaultDate]);

    const reset = () => { setTargetUserId(''); setSwapDate(''); setReason(''); };

    const todayStr = useMemo(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    const submit = async () => {
        if (!currentWorkspace) return;
        if (!targetUserId) { showToast({ variant: 'destructive', description: t('swap.err_target') }); return; }
        if (!swapDate) { showToast({ variant: 'destructive', description: t('swap.err_date') }); return; }
        setBusy(true);
        try {
            await shiftSwapService.request({
                workspaceId: currentWorkspace._id,
                targetUserId,
                swapDate,
                reason: reason.trim() || undefined,
            });
            showToast({ description: t('swap.toast_created') });
            reset();
            onOpenChange(false);
            onCreated?.();
        } catch (e: any) {
            showToast({ variant: 'destructive', description: e?.response?.data?.error || t('swap.toast_failed') });
        } finally {
            setBusy(false);
        }
    };

    return (
        <BottomSheet
            open={open}
            onOpenChange={onOpenChange}
            title={t('swap.title')}
            subtitle={t('swap.subtitle')}
            footer={
                <Button onClick={submit} disabled={busy} className="w-full">
                    <Repeat className="h-4 w-4 mr-1.5" /> {t('swap.submit')}
                </Button>
            }
        >
            <div className="space-y-4">
                <div>
                    <label className="text-xs font-bold text-gray-700 mb-1.5 block">{t('swap.target')}</label>
                    <Select value={targetUserId} onValueChange={setTargetUserId}>
                        <SelectTrigger className="h-10"><SelectValue placeholder={t('swap.target_placeholder')} /></SelectTrigger>
                        <SelectContent>
                            {members.map((m) => (
                                <SelectItem key={m._id || m.id} value={m._id || m.id}>{m.name || m.email}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <div>
                    <label className="text-xs font-bold text-gray-700 mb-1.5 block">{t('swap.date')}</label>
                    <DatePicker value={swapDate} onChange={setSwapDate} minDate={todayStr} placeholder={t('swap.date_placeholder')} />
                </div>

                <div>
                    <label className="text-xs font-bold text-gray-700 mb-1.5 block">{t('swap.reason')}</label>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        placeholder={t('swap.reason_placeholder')}
                        className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400 resize-none"
                    />
                </div>
            </div>
        </BottomSheet>
    );
}
